// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { Setting } from '@api/types';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '@errorHandler/errors';
import { Button, Drawer, Input } from '@imprivata-cloud/design-system';
import { Spin, message } from 'antd';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEndpointBehavior } from '../hooks/useEndpointBehavior';
import { buildCustomSettingsChanges } from '../utils/buildCustomSettingsChanges';

const TRANSLATION_ROOT = 'portal.settings.endpoints.behavior-drawer';
const CUSTOM_SETTINGS_CATEGORY = 'custom-settings';
// configsvc rejects 'string'; the settings engine expects 'value' (see Security).
const CUSTOM_SETTING_VALUE_TYPE = 'value';
const BEHAVIOR_FEATURE_KEY = 'eamc';
const BEHAVIOR_CONFIG_TYPE = 'advanced-settings';
// Seconds the save toast stays visible (antd default is 3s, which felt too short).
const SAVE_SUCCESS_DURATION_S = 6;

type EndpointBehaviorDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const emptyRow = (): Setting => ({
  featureKey: BEHAVIOR_FEATURE_KEY,
  configType: BEHAVIOR_CONFIG_TYPE,
  categoryName: CUSTOM_SETTINGS_CATEGORY,
  name: '',
  value: '',
  valueType: CUSTOM_SETTING_VALUE_TYPE,
});

/**
 * Loads the custom-settings category and edits it as Name/Value rows in local
 * state. Apply persists a per-row diff (upsert/delete) via changeCustomSettings;
 * Cancel/dismiss discards edits and returns to the list without touching its
 * state (REQ-041/042). Local edits are seeded from the loaded config each open.
 */
export const EndpointBehaviorDrawer = ({ open, onClose }: EndpointBehaviorDrawerProps) => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const [messageApi, contextHolder] = message.useMessage();
  const { config, change } = useEndpointBehavior(open);

  const [rows, setRows] = useState<Setting[]>([]);
  const original = (config.data?.settings ?? []).filter(
    s => s.categoryName === CUSTOM_SETTINGS_CATEGORY,
  );

  // External sync: reseed the editable rows from the freshly loaded config.
  useEffect(() => {
    if (config.data) {
      setRows(config.data.settings.filter(s => s.categoryName === CUSTOM_SETTINGS_CATEGORY));
    }
  }, [config.data]);

  const updateRow = (index: number, patch: Partial<Pick<Setting, 'name' | 'value'>>) => {
    setRows(prev => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addRow = () => setRows(prev => [...prev, emptyRow()]);
  const removeRow = (index: number) => setRows(prev => prev.filter((_, i) => i !== index));

  // Cancel/dismiss discards uncommitted edits by resetting rows to the loaded
  // config, so stale edits never flash on reopen even from cache (REQ-041).
  const handleClose = () => {
    setRows(original);
    onClose();
  };

  const handleApply = () => {
    const changes = buildCustomSettingsChanges(original, rows.filter(r => r.name.trim()));
    if (changes.length === 0) {
      handleClose();
      return;
    }
    change.mutate(
      { changes },
      {
        // Keep the drawer open after a save so further edits can be made; surface
        // a longer-lived success toast instead of dismissing the context.
        onSuccess: () => {
          void messageApi.success(t(`${TRANSLATION_ROOT}.save-success`), SAVE_SUCCESS_DURATION_S);
        },
        onError: e => emitError(new AppError(e)),
      },
    );
  };

  return (
    <Drawer open={open} onOpenChange={(next: boolean) => !next && handleClose()}>
      <Drawer.Panel
        title={t(`${TRANSLATION_ROOT}.title`)}
        description={t(`${TRANSLATION_ROOT}.description`)}
        width="690px"
      >
        {contextHolder}
        {config.isLoading && <Spin />}
        {config.isError && (
          <p className="endpoints-drawer-error">{t(`${TRANSLATION_ROOT}.load-error`)}</p>
        )}
        {config.data && (
          <>
            <div className="endpoints-behavior-rows">
              {rows.map((row, index) => (
                <div key={row.settingId ?? `new-${index}`} className="endpoints-behavior-row">
                  <Input
                    label={t(`${TRANSLATION_ROOT}.name-label`)}
                    value={row.name}
                    onChange={e => updateRow(index, { name: e.target.value })}
                  />
                  <Input
                    label={t(`${TRANSLATION_ROOT}.value-label`)}
                    value={row.value}
                    onChange={e => updateRow(index, { value: e.target.value })}
                  />
                  <Button
                    variant="glyph"
                    icon={<Trash2 size={16} />}
                    aria-label={t(`${TRANSLATION_ROOT}.remove-row`)}
                    onClick={() => removeRow(index)}
                  >
                    {''}
                  </Button>
                </div>
              ))}
              <div className="endpoints-behavior-add">
                <Button variant="text" size="sm" icon={<Plus size={16} />} onClick={addRow}>
                  {t(`${TRANSLATION_ROOT}.add-row`)}
                </Button>
              </div>
            </div>
            <div className="endpoints-drawer-footer">
              <Button variant="default" onClick={handleClose}>
                {t(`${TRANSLATION_ROOT}.cancel`)}
              </Button>
              <Button variant="primary" onClick={handleApply} disabled={change.isPending}>
                {t(`${TRANSLATION_ROOT}.apply`)}
              </Button>
            </div>
          </>
        )}
      </Drawer.Panel>
    </Drawer>
  );
};
